/**
 * WHAT A PRINTED PAGE SAYS ABOUT ITSELF.
 *
 * Shane, 2026-09-26, after deciding the platform itself is the controlled
 * document rather than a numbered entry in a register:
 *
 *   *"So we are not doing the doc number on a printed document as before. We
 *   are doing the printed by and date. in the top right corner and on the
 *   bottom right we have powered by JeanieIQ on any print areas."*
 *
 * WHY THIS IS THE WHOLE CONTROL. An auditor's question about documented
 * information is whether the wrong version can be used. On a shared drive that
 * is hard — twelve files called `Plant Register v3 FINAL.xlsx`, any of them
 * editable. A platform deletes the question: one record, always current,
 * role-gated, every change audit-logged. Shane's own comparison: *"we never
 * get asked to provide anything round sage as to how it controls its
 * invoices"*.
 *
 * So the paper is the only uncontrolled thing, and the only thing it has to
 * declare is HOW OLD IT IS and WHO TOOK IT. A sheet found in a van two years
 * later announces its age instead of passing as current.
 *
 * ON EVERY SHEET, not just the first. `position: fixed` inside a print
 * stylesheet repeats on each printed page in every browser the platform
 * supports, which is exactly what a page detached from the staple needs.
 *
 * ONLY WHEN PRINTING. The stamp is meaningless on screen — the screen is
 * always current — so it is hidden until the print dialog runs, and takes no
 * space in the layout the rest of the time.
 *
 * WHAT THIS IS NOT FOR. A document imported from JeanieIQ Admin — a policy, a
 * memo, a certificate — is a filed document and keeps its own control line:
 * `DOC ## · POL ## · Issue · date · approved by`. Shane: *"we leave the
 * documents alone that have the doc ## mem ## as they are imported documents
 * etc that need to be controlled"*. Those print BOTH: their control line, and
 * this. See `documentControlFooter.ts` for the other half.
 */
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { fetchMe } from '../lib/meApi';
import { queryClient } from '../lib/queryClient';
import { companyTimeZone, formatInZone, useCompanyClock } from '../lib/companyClock';

/**
 * "26 September 2026, 14:32" — the way a person writes a moment down, on the
 * COMPANY's clock (TODO 135): a sheet printed in another zone still says the
 * time the company lives by.
 */
function printedAtLabel(now: Date, timeZone: string): string {
  return formatInZone(timeZone, now, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }, 'en-GB');
}

/**
 * THE SAME STAMP AS AN HTML STRING, for the print paths that are not React.
 *
 * A dozen surfaces build a document as HTML and open it in a new window —
 * `docExport`, the debtor and subcontract letters,
 * the plant and maintenance registers. A component cannot reach into those, so
 * they get this.
 *
 * ONE SOURCE, TWO RENDERERS. Both live in this file on purpose: two
 * definitions of the stamp is how one of them quietly stops matching the
 * other, and the whole point is that every printed page says the same thing.
 *
 * THE NAME COMES FROM THE CACHE, synchronously. `AppShell` has already
 * fetched the signed-in user by the time anybody prints, and these helpers are
 * plain functions with nowhere to await. A cache miss costs the name, not the
 * date — and the date is most of the value.
 */
export function printStampHtml(now: Date = new Date()): string {
  const me = queryClient.getQueryData<{ user?: { firstName?: string; lastName?: string } }>([
    'auth',
    'me',
  ]);
  const who = me?.user ? `${me.user.firstName ?? ''} ${me.user.lastName ?? ''}`.trim() : '';
  const when = printedAtLabel(now, companyTimeZone());
  const printed = who ? `Printed ${when} by ${who}` : `Printed ${when}`;
  const corner = 'position:fixed;font-size:8pt;color:#555;z-index:9999;';
  /* Escaped: a person's name reaches this from the database, and a document
   * that renders somebody's surname as markup is a bug at best. */
  const esc = (t: string): string =>
    t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return (
    `<div style="${corner}top:6mm;right:10mm;text-align:right;">${esc(printed)}</div>` +
    `<div style="${corner}bottom:6mm;right:10mm;text-align:right;">Powered by JeanieIQ</div>`
  );
}

/**
 * THE SAME STAMP AS ONE LINE OF TEXT, for an output with no corners: the first
 * line of a CSV the browser builds, the foot of a saved picture. Worded as the
 * server's spreadsheet stamp is (`printStampLine` + "Powered by JeanieIQ"),
 * so a file says the same thing whichever side made it.
 */
export function printStampText(now: Date = new Date()): string {
  const me = queryClient.getQueryData<{ user?: { firstName?: string; lastName?: string } }>([
    'auth',
    'me',
  ]);
  const who = me?.user ? `${me.user.firstName ?? ''} ${me.user.lastName ?? ''}`.trim() : '';
  const when = printedAtLabel(now, companyTimeZone());
  return `${who ? `Printed ${when} by ${who}` : `Printed ${when}`}  ·  Powered by JeanieIQ`;
}

const CORNER: React.CSSProperties = {
  position: 'fixed',
  fontSize: '8pt',
  color: '#555',
  zIndex: 9999,
};

export function PrintStamp({
  /** Overrides the clock. Tests pin it; nothing else passes it. */
  now,
}: {
  now?: Date;
} = {}): JSX.Element {
  /*
   * The signed-in user. Shared query key, so a page that already knows who is
   * logged in does not fetch twice — and a failure is not fatal: the date
   * alone still ages the sheet, which is most of the value. Never a
   * placeholder name: "Printed by —" reads as printed by nobody.
   */
  const meQ = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const { timeZone } = useCompanyClock();
  const u = meQ.data?.user;
  const who = u ? `${u.firstName} ${u.lastName}`.trim() : '';
  const when = printedAtLabel(now ?? new Date(), timeZone);

  return (
    <>
      {/* The `.jiq-print-stamp` rule lives in `styles/theme.css` — hidden on
          screen, shown on every sheet when printed. Not inlined here: the
          class guard reads the stylesheets to catch typos, and a class it
          cannot see is a class it cannot check. The `@page` margin is left to
          the browser, since several print views set their own. */}
      <div
        className="jiq-print-stamp"
        style={{ ...CORNER, top: '6mm', right: '10mm', textAlign: 'right' }}
        data-testid="print-stamp-printed-by"
      >
        {who ? `Printed ${when} by ${who}` : `Printed ${when}`}
      </div>
      <div
        className="jiq-print-stamp"
        style={{ ...CORNER, bottom: '6mm', right: '10mm', textAlign: 'right' }}
        data-testid="print-stamp-powered-by"
      >
        Powered by JeanieIQ
      </div>
    </>
  );
}
